/**
 * Central helpers for the two DUA declaration kinds: export (exportacion,
 * tipo 32) and import (importacion, tipo 64).
 *
 * The whole frontend was originally export-only. Rather than sprinkle
 * `duas.exportaciones` / `exportacionesIndex` literals everywhere, all kind
 * discrimination goes through this module so the two shapes stay consistent.
 */

export const EXPORT = 'export';
export const IMPORT = 'import';

// Declaration `tipo` values per the backend extraction schemas.
export const EXPORT_TIPO = 32;
export const IMPORT_TIPO = 64;

/**
 * Normalize any value to a valid kind, defaulting to export.
 * @param {unknown} value
 * @returns {'export'|'import'}
 */
export function normalizeKind(value) {
  return value === IMPORT ? IMPORT : EXPORT;
}

/**
 * Derive the kind of a single declaration object from its own content.
 * Prefers the unambiguous discriminators the backend always sets:
 *   - tipo 64 / declaracion_1 "IM" => import
 *   - tipo 32 / declaracion_1 "EX" => export
 * Falls back to export when nothing indicates import.
 *
 * @param {Record<string, any>|null|undefined} declaration
 * @returns {'export'|'import'}
 */
export function getDeclarationKind(declaration) {
  if (!declaration || typeof declaration !== 'object') return EXPORT;

  const tipo = declaration.tipo;
  const tipoNum = typeof tipo === 'string' ? parseInt(tipo, 10) : tipo;
  if (tipoNum === IMPORT_TIPO) return IMPORT;
  if (tipoNum === EXPORT_TIPO) return EXPORT;

  const decl1 = typeof declaration.declaracion_1 === 'string'
    ? declaration.declaracion_1.trim().toUpperCase()
    : '';
  if (decl1 === 'IM') return IMPORT;
  if (decl1 === 'EX') return EXPORT;

  return EXPORT;
}

/**
 * Derive the kind for a set of extracted declarations (uses the first).
 * @param {Array<Record<string, any>>|null|undefined} declarations
 * @returns {'export'|'import'}
 */
export function getKindFromDeclarations(declarations) {
  if (!Array.isArray(declarations) || declarations.length === 0) return EXPORT;
  const first = declarations.find((d) => d && typeof d === 'object');
  return getDeclarationKind(first);
}

/**
 * The `duas` array key for a kind: 'exportaciones' | 'importaciones'.
 * @param {'export'|'import'} kind
 */
export function duasKey(kind) {
  return normalizeKind(kind) === IMPORT ? 'importaciones' : 'exportaciones';
}

/**
 * The per-declaration index field name the TARIC API returns in
 * success/error rows: 'exportacionesIndex' | 'importacionesIndex'.
 * @param {'export'|'import'} kind
 */
export function indexKey(kind) {
  return normalizeKind(kind) === IMPORT ? 'importacionesIndex' : 'exportacionesIndex';
}

/**
 * Read the declarations array out of a `taric.duas` object regardless of kind.
 * @param {Record<string, any>|null|undefined} taric a `{ duas: {...} }` payload
 * @returns {{ kind: 'export'|'import', key: string, declarations: any[] }}
 */
export function readDuasDeclarations(taric) {
  const duas = taric?.duas;
  if (duas && Array.isArray(duas.importaciones)) {
    return { kind: IMPORT, key: 'importaciones', declarations: duas.importaciones };
  }
  return {
    kind: EXPORT,
    key: 'exportaciones',
    declarations: Array.isArray(duas?.exportaciones) ? duas.exportaciones : [],
  };
}

/**
 * Read the index field from a TARIC success/error row for either kind.
 * @param {Record<string, any>|null|undefined} row
 * @returns {number|undefined}
 */
export function readRowIndex(row) {
  if (!row || typeof row !== 'object') return undefined;
  return row.exportacionesIndex ?? row.importacionesIndex;
}

/**
 * Read the nested per-row field-error object (errors are nested under the
 * dua key, e.g. `errorObj.exportaciones` or `errorObj.importaciones`).
 * @param {Record<string, any>|null|undefined} row
 */
export function readRowDeclarationErrors(row) {
  if (!row || typeof row !== 'object') return undefined;
  return row.importaciones ?? row.exportaciones;
}
